import { z } from "zod";

import {
  addressSchema,
  dateString,
  endDateAfterStart,
  optionalEmail,
  optionalPhone,
  optionalText,
  requiredDate,
  requiredText
} from "@/lib/forms/schema";

import type {
  CreateForeignNationalEmploymentHistoryEntryRequest,
  EmployerInfo,
  ForeignNationalEmploymentHistoryEntry
} from "../../../../api/types/foreignNationalEmploymentHistory";
import {
  addressFromRaw,
  addressToPayload,
  addressesEqual,
  emptyAddress,
  type AddressValues
} from "../../lib/address";
import { normalizeText, toDateInputValue } from "../../lib/format";
import { byCurrentThenStartDesc } from "../../lib/records";

export type EmployerValues = {
  legalBusinessName: string;
  dba: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  businessAddress: AddressValues;
};

export type EmploymentDraft = {
  employer: EmployerValues;
  jobTitle: string;
  jobDescription: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  notes: string;
};

export type EmploymentPayload =
  CreateForeignNationalEmploymentHistoryEntryRequest;

export type EmployerTextField = Exclude<
  keyof EmployerValues,
  "businessAddress"
>;

export const emptyEmployer = (): EmployerValues => ({
  legalBusinessName: "",
  dba: "",
  contactName: "",
  contactTitle: "",
  contactEmail: "",
  contactPhone: "",
  businessAddress: emptyAddress()
});

export const emptyEmploymentDraft = (): EmploymentDraft => ({
  employer: emptyEmployer(),
  jobTitle: "",
  jobDescription: "",
  startDate: "",
  endDate: "",
  isCurrent: false,
  notes: ""
});

const employerFromRaw = (
  raw: EmployerInfo | null | undefined
): EmployerValues => ({
  legalBusinessName: normalizeText(raw?.legalBusinessName),
  dba: normalizeText(raw?.dba),
  contactName: normalizeText(raw?.contactName),
  contactTitle: normalizeText(raw?.contactTitle),
  contactEmail: normalizeText(raw?.contactEmail),
  contactPhone: normalizeText(raw?.contactPhone),
  businessAddress: addressFromRaw(raw?.businessAddress)
});

export const employmentEntryToDraft = (
  e: ForeignNationalEmploymentHistoryEntry
): EmploymentDraft => ({
  employer: employerFromRaw(e.employer),
  jobTitle: normalizeText(e.job_title),
  jobDescription: normalizeText(e.job_description),
  startDate: toDateInputValue(e.start_date),
  endDate: toDateInputValue(e.end_date),
  isCurrent: Boolean(e.is_current),
  notes: normalizeText(e.notes)
});

const employersEqual = (a: EmployerValues, b: EmployerValues): boolean =>
  normalizeText(a.legalBusinessName) === normalizeText(b.legalBusinessName) &&
  normalizeText(a.dba) === normalizeText(b.dba) &&
  normalizeText(a.contactName) === normalizeText(b.contactName) &&
  normalizeText(a.contactTitle) === normalizeText(b.contactTitle) &&
  normalizeText(a.contactEmail) === normalizeText(b.contactEmail) &&
  normalizeText(a.contactPhone) === normalizeText(b.contactPhone) &&
  addressesEqual(a.businessAddress, b.businessAddress);

export const employmentDraftsEqual = (
  a: EmploymentDraft,
  b: EmploymentDraft
): boolean =>
  employersEqual(a.employer, b.employer) &&
  normalizeText(a.jobTitle) === normalizeText(b.jobTitle) &&
  normalizeText(a.jobDescription) === normalizeText(b.jobDescription) &&
  normalizeText(a.startDate) === normalizeText(b.startDate) &&
  normalizeText(a.endDate) === normalizeText(b.endDate) &&
  a.isCurrent === b.isCurrent &&
  normalizeText(a.notes) === normalizeText(b.notes);

/** Employer, contact, address and role are all required; the end date isn't. */
export const employmentDraftSchema: z.ZodType<
  EmploymentDraft,
  EmploymentDraft
> = endDateAfterStart(
  z.object({
    employer: z.object({
      legalBusinessName: requiredText("Business name"),
      dba: optionalText(),
      contactName: requiredText("Contact name"),
      contactTitle: requiredText("Contact title"),
      contactEmail: requiredText("Contact email").and(optionalEmail()),
      contactPhone: requiredText("Contact phone").and(optionalPhone()),
      businessAddress: addressSchema({ required: true })
    }),
    jobTitle: requiredText("Job title"),
    jobDescription: requiredText("Job description"),
    startDate: requiredDate("From date"),
    endDate: dateString(),
    isCurrent: z.boolean(),
    notes: optionalText()
  }),
  "startDate",
  "endDate",
  "To date can't be before the from date."
);

const employerToPayload = (e: EmployerValues): EmployerInfo | null => {
  const legalBusinessName = normalizeText(e.legalBusinessName);
  const dba = normalizeText(e.dba);
  const contactName = normalizeText(e.contactName);
  const contactTitle = normalizeText(e.contactTitle);
  const contactEmail = normalizeText(e.contactEmail);
  const contactPhone = normalizeText(e.contactPhone);
  const businessAddress = addressToPayload(e.businessAddress);
  if (
    !legalBusinessName &&
    !dba &&
    !contactName &&
    !contactTitle &&
    !contactEmail &&
    !contactPhone &&
    !businessAddress
  ) {
    return null;
  }
  return {
    legalBusinessName: legalBusinessName || null,
    dba: dba || null,
    contactName: contactName || null,
    contactTitle: contactTitle || null,
    contactEmail: contactEmail || null,
    contactPhone: contactPhone || null,
    businessAddress
  };
};

export const employmentDraftToPayload = (
  d: EmploymentDraft
): EmploymentPayload => ({
  employer: employerToPayload(d.employer),
  job_title: normalizeText(d.jobTitle) || null,
  job_description: normalizeText(d.jobDescription) || null,
  start_date: normalizeText(d.startDate),
  end_date: d.isCurrent ? null : normalizeText(d.endDate) || null,
  is_current: d.isCurrent,
  notes: normalizeText(d.notes) || null
});

export const sortEmploymentEntries =
  byCurrentThenStartDesc<ForeignNationalEmploymentHistoryEntry>(
    (e) => e.start_date,
    (e) => Boolean(e.is_current)
  );

export const employerLabel = (
  e: ForeignNationalEmploymentHistoryEntry
): string =>
  normalizeText(e.employer?.legalBusinessName) ||
  normalizeText(e.employer?.dba) ||
  "—";

export const employmentFocusDomId = (id: string): string =>
  `employment-history-${id}`;
